import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import {
  GoAButton,
  GoACheckbox,
  GoAFormItem,
  GoASpacer,
} from '@abgov/react-components';
import styles from './onboarding.module.css';
import useCsrService from '../../services/csr-api-redux';
import { useNavigate } from 'react-router-dom';
import { loadCsrUser } from '../../state/slices/authSlice';
import {
  fetchAllAgreements,
  fetchLatestUserAgreementAccepted,
  fetchUserAgreements,
  publishUserAgreement,
} from '../../state/slices/agreementSlice';
import { NotificationType } from '../notifications/notification-component';

interface LawEnforcementTosComponentProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const LawEnforcementTosComponent =
  ({}: LawEnforcementTosComponentProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const csrService = useCsrService();
    const stateUserAgreements = useAppSelector(
      (state: RootState) => state.agreements,
    );
    const stateAuth = useAppSelector((state: RootState) => state.auth);
    const termsAndConditionsCheckboxFormErrorText =
      'You must agree to the terms and conditions before proceeding.';

    const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] =
      useState(false);
    const [
      termsAndConditionsCheckboxFormError,
      setTermsAndConditionsCheckboxFormError,
    ] = useState<boolean>(false);

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      if (!stateAuth.auth.access.access) navigate('/');

      if (!stateAuth.auth.csrUser && !stateAuth.isLoading) {
        dispatch(loadCsrUser({ csrService }));
        return;
      }

      if (stateAuth.isLoading || !stateAuth.auth.csrUser) return;

      if (stateUserAgreements.latestUserAgreementAccepted) {
        navigate('/law/dashboard');
      }
    }, [
      stateAuth.auth.csrUser,
      stateUserAgreements.latestUserAgreementAccepted,
      stateAuth.isLoading,
      navigate,
    ]);

    useEffect(() => {
      dispatch(fetchAllAgreements(csrService));
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        setIsVisible(window.scrollY > 150);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleCheckboxChange = (checked: boolean) => {
      setTermsAndConditionsCheckbox(checked);
      setTermsAndConditionsCheckboxFormError(false);
    };

    const validate = (): boolean => {
      let isValid = true;
      dispatch(removeAllValidationErrorNotifications());

      if (!termsAndConditionsCheckbox) {
        setTermsAndConditionsCheckboxFormError(true);
        isValid = false;
      }
      return isValid;
    };

    const save = async () => {
      await dispatch(
        publishUserAgreement({
          csrService,
          agreementId: stateUserAgreements.agreements.find(
            (x) => x.isLawEnforcement,
          )?.id!,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(fetchLatestUserAgreementAccepted(csrService));
        })

        .catch((error) => {
          dispatch(removeAllValidationErrorNotifications());
          dispatch(
            pushNotification({
              body: 'Failed to publish user agreement',
              notificationType: NotificationType.importantGenericError,
            }),
          );
          console.error('Error publishing user agreement:', error);
        });
    };

    return (
      <div>
        <div>
          <GoASpacer vSpacing={'xl'} />
          <h1>Terms of Use</h1>
          <h3>Government of Alberta Report My Collision Online Service</h3>
          <GoASpacer vSpacing={'s'} />
          <h4>This version in effect since May 21, 2025</h4>
          <GoASpacer vSpacing={'s'} />
          <p>
            You must accept these Terms of Use before using this Online Service
            to view or obtain a Validated Report My Collision Package. If you do
            not accept, you cannot proceed.
          </p>

          <ol>
            <li>
              Introduction
              <GoASpacer vSpacing={'s'} />
              <p>
                This process and available services can be used only by an
                authorized employee of a law enforcement agency or a Government
                of Alberta department to access collision-related information
                for valid law enforcement purposes under the Traffic Safety Act
                (TSA) and Access to Motor Vehicle Information Regulation
                (AMVIR).
              </p>
            </li>
            <li>
              Governing Law and Legal Framework
              <GoASpacer vSpacing={'s'} />
              <p>
                These Terms of Use are governed by and interpreted in accordance
                with the laws in force in Alberta, and you agree and submit to
                the exclusive jurisdiction of courts in Alberta.
                <GoASpacer vSpacing={'s'} />
                You acknowledge that the Registrar collects information for
                Traffic Safety Act (TSA) purposes, and the Access to Motor
                Vehicle Information Regulation (AMVIR). The Registrar may use
                the information collected to monitor and audit the request and
                release of information, and to conduct investigations related to
                any specific request or use of information.
              </p>
            </li>
            <li>
              Accountability
              <GoASpacer vSpacing={'s'} />
              <p>
                You are responsible for ensuring that the information you
                provide is complete, accurate and current. Any false,
                inaccurate, or misleading information will be considered a
                breach of these Terms of Use. Your ability to request a
                Validated Report My Collision Package may be terminated.
                <GoASpacer vSpacing={'s'} />
                You agree to be responsible for any unauthorized request,
                collection, use and disclosure of personal information that
                results from inappropriate request, access or use of the
                information released by the Registrar.
              </p>
            </li>
            <li>
              Information provided
              <GoASpacer vSpacing={'s'} />
              <p>
                The Registrar does not automatically collect any specific
                personal or business information from you, such as your name,
                agency name and mailing address, phone or fax number, or email
                address. The personal or business information you do provide is
                collected for TSA and AMVIR purposes, and will be used for the
                following purposes:
              </p>
              <ul className="goa-unordered-list">
                <li>
                  To confirm your identity and your employer every time you
                  order information or records using this Online Service.
                </li>
                <li>To monitor any request, release, or use of information.</li>
                <li>
                  To permit the Registrar to audit any request, release, or use
                  of information.
                </li>
                <li>
                  To permit the Registrar to investigate complaints related to
                  any request, release, or use of information.
                </li>
              </ul>
              <GoASpacer vSpacing={'s'} />
              <p>
                If you have questions about the collection of information,
                please contact the appropriate office of the Registrar:
              </p>
              <p>
                1. Service Alberta & Red Tape Reduction, Motor Vehicles: (780)
                427-0024
                <GoASpacer vSpacing={'s'} />
                2. Transportation & Economic Corridors, Traffic Safety Services:{' '}
                <a href="mailto:safetysystemssupport@gov.ab.ca">
                  safetysystemssupport@gov.ab.ca
                </a>
              </p>
            </li>
            <li>
              No warranties
              <GoASpacer vSpacing={'s'} />
              <p>
                This Online Service is provided “as is” without warranty of any
                kind, including warranty of fitness for a particular purpose.
                The Registrar does not warrant the accuracy or the completeness
                of any release of information, or that this Online Service will
                function without error, failure, or interruption.
              </p>
            </li>

            <li>
              Limitation of Liability
              <GoASpacer vSpacing={'s'} />
              <p>
                No action may be brought by any person against the Registrar for
                any loss or damage of any kind caused by any reason or purpose
                including, without limitation, reliance on the functioning of
                this Online Service.
              </p>
            </li>

            <li>
              Discontinuance of Service
              <GoASpacer vSpacing={'s'} />
              <p>
                The Registrar may discontinue this Online Service at any time
                without notice.
              </p>
            </li>

            <li>
              Remedies Available to the Registrar
              <GoASpacer vSpacing={'s'} />
              <p>
                If you breach these Terms, the Registrar may cancel or suspend
                your access without notice.
              </p>
            </li>

            <li>
              Purpose of this Online Service
              <GoASpacer vSpacing={'s'} />
              <p>
                This Online Service is solely for law enforcement and authorized
                public bodies to access collision-related data. It must not be
                used for any other purpose.
              </p>
            </li>

            <li>
              Amendments
              <GoASpacer vSpacing={'s'} />
              <p>
                The Registrar may amend these Terms of Use at any time. You will
                have the opportunity to review the amended Terms of Use and
                either accept the amended Terms of Use or stop using this Online
                Service. You acknowledge that if you decide to stop using this
                Online Service the Registrar continues to maintain the authority
                to monitor, audit, or investigate any previous request, release,
                or use of information.
              </p>
            </li>

            <li>
              Linked websites
              <GoASpacer vSpacing={'s'} />
              <p>
                This Online Services may involve third-party service providers
                or may contain links to third-party sites. The Registrar
                provides these links only as a service and convenience and does
                not take any responsibility for the content at the third-party
                sites. A link to these sites does not imply an endorsement or
                recommendation of the products, services, or information found
                there.
                <GoASpacer vSpacing={'s'} />
                A third-party service provider or website may have a privacy
                policy different from that of the Registrar and may provide less
                security than this Online Service. The Registrar disclaims all
                liability about your use of third-party service provider or
                access to linked web sites.
                <GoASpacer vSpacing={'s'} />
                The Registrar reserves the right to regularly review and
                re-evaluate all links or service providers and reserves the
                right to terminate any link or service provider without prior
                notification.
              </p>
            </li>

            <li>
              Communications
              <GoASpacer vSpacing={'s'} />
              <p>
                The Registrar may send faxes, emails or contact you using the
                contact information you provided, including communications about
                your use of this Online Service and any request, release, or use
                of information.
              </p>
            </li>
          </ol>
          <div
            className={`${styles.stickyFooter} ${isVisible ? styles.visible : ''}`}
          >
            <GoAFormItem
              label=""
              error={
                termsAndConditionsCheckboxFormError
                  ? termsAndConditionsCheckboxFormErrorText
                  : ''
              }
              mb="none"
            >
              <GoACheckbox
                name="termsCheckbox"
                checked={termsAndConditionsCheckbox}
                onChange={(_, clicked) => handleCheckboxChange(clicked)}
                error={termsAndConditionsCheckboxFormError}
              >
                I agree to the Terms of use
              </GoACheckbox>
            </GoAFormItem>
            <GoAButton
              onClick={() => {
                if (validate()) {
                  save();
                }
              }}
              width="300px"
            >
              Proceed to dashboard
            </GoAButton>
          </div>
        </div>
      </div>
    );
  };
